import { useState, useContext } from 'react';
import { PaymentMethod } from '@stripe/stripe-js';
import { Product } from 'src/pages/deprecated/payment/types';
import { ToastContext } from 'src/components/toast/toast';
import User from 'src/models/User';
import { useDecision } from '@optimizely/react-sdk';
import { useDispatch } from 'react-redux';
import { checkAuthentication } from 'src/lib/auth/actions';
import {
  getNutritionCoachProduct,
  getNutritionCoachProductPrice,
} from '../utils/challengeAddon.util';
import { extractErrorMessage, isUserFromAustralia } from 'src/lib/utils';
import { useAppSelector } from 'src/store/hooks';
import moment from 'moment';
import ProductService from 'src/services/product-service';
import { useAfterpayPayment } from './useAfterpayPayment';
import { useReactivateSubscription } from 'src/pages/account/sections/subscription/update-subscription/useReactivateSubscription';
import { AxiosError } from 'axios';
import {
  trackAddonSelected,
  trackAddonRemoved,
  trackAddonPaymentStepViewed,
  trackAddonPaymentMethodSelected,
  trackAddonPaymentSubmitted,
  trackAddonPaymentFailed,
  trackAddonPaymentCompleted,
  trackAddonPurchaseCompleted,
} from 'src/util/track';

interface UseNutritionCoachAddonParams {
  products: Product[] | undefined;
  user: User | null | undefined;
  paymentMethod: PaymentMethod | undefined;
  setRegetProducts: (reget: boolean) => void;
}

interface UseNutritionCoachAddonReturn {
  nutritionCoachProduct: Product | undefined;
  showNutritionCoachAddon: boolean;
  hasSubscribedToNutritionCoachAddon: boolean;
  isProcessingNutritionCoach: boolean;
  showPaymentModal: boolean;
  showRemoveConfirmationModal: boolean;
  showSuccessModal: boolean;
  handleAddNutritionCoach: () => Promise<void>;
  handleRemoveNutritionCoach: () => Promise<void>;
  handleOpenPaymentModal: () => void;
  handleClosePaymentModal: () => void;
  handleCloseRemoveConfirmationModal: () => void;
  handleCloseSuccessModal: () => void;
  handleConfirmRemove: () => Promise<void>;
  handleConfirmPurchase: () => Promise<void>;
  setSelectedPaymentType: (type: 'card' | 'afterpay') => void;
  triggerSuccessModal: () => void;
  closePaymentModal: () => void;
  coachingEndDate?: string | null;
  nextBillingDate?: string | null;
  isReactivateAllowed: boolean;
  showReactivateModal: boolean;
  setShowReactivateModal: (show: boolean) => void;
  isReactivating: boolean;
  reactivateError: string;
  handleConfirmReactivateAndPurchase: () => Promise<void>;
}

export const useNutritionCoachAddon = ({
  products,
  user,
  paymentMethod,
  setRegetProducts,
}: UseNutritionCoachAddonParams): UseNutritionCoachAddonReturn => {
  const { addToast } = useContext(ToastContext);
  const reduxDispatch = useDispatch();

  const allSubscriptions = useAppSelector(
    state => state.subscription.allSubscriptions
  );
  const currentSubscription = allSubscriptions?.currentSubscription;
  const nutritionCoachingAddon = currentSubscription?.nutritionCoachingAddon;

  const [isProcessingNutritionCoach, setIsProcessingNutritionCoach] =
    useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRemoveConfirmationModal, setShowRemoveConfirmationModal] =
    useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<
    'card' | 'afterpay'
  >('card');

  const [{ enabled: showNutritionCoachAddonDecisionEnabled }] = useDecision(
    'show_personal_coach_addon_myaccount',
    {
      autoUpdate: true,
    }
  );

  const nutritionPersonalCoachingProduct = getNutritionCoachProduct(
    products || []
  );

  const hasSubscribedToNutritionCoachAddon = Boolean(
    user?.isNutritionCoachingAddOn
  );

  // const hasChallengePersonalCoachAddon =
  //   Boolean(user?.isChallengeUser) &&
  //   Boolean(user?.isCoachingSupport) &&
  //   !hasSubscribedToNutritionCoachAddon;

  const showNutritionCoachAddon =
    nutritionPersonalCoachingProduct &&
    showNutritionCoachAddonDecisionEnabled &&
    // !hasChallengePersonalCoachAddon &&
    isUserFromAustralia();

  // Format the next billing date to YYYY-MM-DD
  // Next Billing Date is in Unix timestamp (seconds)
  // NOTE: Technically, Next Billing is same as the Coaching End Date
  const nextBillingDateDisplay = nutritionCoachingAddon?.nextBillingDate
    ? moment((nutritionCoachingAddon?.nextBillingDate ?? 0) * 1000).format(
        'YYYY-MM-DD'
      )
    : '';

  const nextBillingDateFormatted = hasSubscribedToNutritionCoachAddon
    ? nextBillingDateDisplay
    : null;

  const coachingEndDateFormatted = hasSubscribedToNutritionCoachAddon
    ? null
    : nextBillingDateDisplay;

  const priceInfo = getNutritionCoachProductPrice(products || []);
  const ncPrice = priceInfo?.price || 0;

  // Initialize Afterpay payment hook
  const { initiateAfterpayPayment } = useAfterpayPayment({
    user: user!,
    priceId: priceInfo?.priceId || '',
    price: ncPrice,
    onSuccess: () => {
      setIsProcessingNutritionCoach(false);
      setShowPaymentModal(false);
      // Success is handled by parent's return URL processing
    },
    onError: error => {
      console.error('Afterpay payment initiation error:', error);
      setIsProcessingNutritionCoach(false);
      // Error handling is done in the hook itself
    },
  });

  const handleAddNutritionCoach = async () => {
    trackAddonPaymentStepViewed('nutrition_coaching', ncPrice);
    setShowPaymentModal(true);
  };

  const executePurchase = async () => {
    if (!user || !priceInfo) {
      addToast('Price information not available', 'danger', 5000);
      return;
    }

    trackAddonPaymentSubmitted(
      selectedPaymentType,
      'nutrition_coaching',
      ncPrice
    );

    setIsProcessingNutritionCoach(true);
    try {
      if (selectedPaymentType === 'afterpay') {
        // Use the Afterpay payment hook
        await initiateAfterpayPayment();
      } else {
        // Handle card payment using existing service
        await ProductService.purchaseNutritionCoachingAddon({
          userId: user.id,
          email: user.email,
          paymentMethodId: paymentMethod?.id || '',
          priceIds: [priceInfo.priceId],
          isAfterPay: false,
          isCardPayment: true,
        });

        trackAddonSelected('nutrition_coaching', 'card', ncPrice);
        trackAddonPaymentCompleted('card', 'nutrition_coaching', ncPrice);
        trackAddonPurchaseCompleted(
          'card',
          'nutrition_coaching',
          ncPrice,
          priceInfo.priceId
        );

        setShowPaymentModal(false);
        setShowSuccessModal(true);
      }
    } catch (error) {
      const requiresReactivation =
        (error as unknown as AxiosError)?.response?.data?.data
          ?.requiresMainSubscriptionReactivation ||
        (error as unknown as AxiosError)?.response?.data
          ?.requiresMainSubscriptionReactivation;

      if (requiresReactivation) {
        setShowReactivateModal(true);
      } else {
        const message = extractErrorMessage(
          error,
          'Failed to add Nutrition Coach addon'
        );
        trackAddonPaymentFailed(
          selectedPaymentType,
          'nutrition_coaching',
          'purchase_request',
          message,
          ncPrice
        );
        addToast(message, 'danger', 5000);
        console.error('Failed to add Personal Coach addon:', error);
      }
    } finally {
      setIsProcessingNutritionCoach(false);
    }
  };

  const {
    isReactivateAllowed,
    reactivatePopupVisible: showReactivateModal,
    setReactivatePopupVisible: setShowReactivateModal,
    isReactivating,
    reactivateError,
    handleReactivateConfirm: handleConfirmReactivateAndPurchase,
  } = useReactivateSubscription(
    user || undefined,
    allSubscriptions || undefined,
    {
      onSuccess: async () => {
        setShowReactivateModal(false);
        await executePurchase();
      },
    }
  );

  const handleConfirmPurchase = async () => {
    if (!user || !nutritionPersonalCoachingProduct) {
      addToast('User or product not available', 'danger', 5000);
      return;
    }

    if (selectedPaymentType === 'card' && !paymentMethod) {
      addToast('Payment method not available', 'danger', 5000);
      return;
    }

    if (isReactivateAllowed) {
      setShowReactivateModal(true);
      return;
    }

    await executePurchase();
  };

  const handleRemoveNutritionCoach = async () => {
    setShowRemoveConfirmationModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!user) {
      addToast('User not found', 'danger', 5000);
      return;
    }

    setIsProcessingNutritionCoach(true);
    try {
      await ProductService.removeNutritionCoachingAddon({
        userId: user.id,
        email: user.email,
      });

      trackAddonRemoved('nutrition_coaching');

      addToast('Coaching Add-On has been removed', 'success', 5000);
      reduxDispatch(checkAuthentication());
      setRegetProducts(true);
      setShowRemoveConfirmationModal(false);
    } catch (error) {
      const message = extractErrorMessage(
        error,
        'Failed to remove Nutrition Coach addon'
      );
      addToast(message, 'danger', 5000);
      console.error('Failed to remove Personal Coach addon:', error);
    } finally {
      setIsProcessingNutritionCoach(false);
    }
  };

  const handleCloseSuccessModal = () => {
    reduxDispatch(checkAuthentication());
    setShowSuccessModal(false);
    setRegetProducts(true);
  };

  const handleSetSelectedPaymentType = (type: 'card' | 'afterpay') => {
    trackAddonPaymentMethodSelected(type, 'nutrition_coaching');
    setSelectedPaymentType(type);
  };

  return {
    nutritionCoachProduct: nutritionPersonalCoachingProduct,
    showNutritionCoachAddon: showNutritionCoachAddon || false,
    hasSubscribedToNutritionCoachAddon:
      hasSubscribedToNutritionCoachAddon || false,
    isProcessingNutritionCoach,
    showPaymentModal,
    showRemoveConfirmationModal,
    showSuccessModal,
    handleAddNutritionCoach,
    handleRemoveNutritionCoach,
    handleOpenPaymentModal: () => setShowPaymentModal(true),
    handleClosePaymentModal: () => setShowPaymentModal(false),
    handleCloseRemoveConfirmationModal: () =>
      setShowRemoveConfirmationModal(false),
    handleCloseSuccessModal,
    handleConfirmRemove,
    handleConfirmPurchase,
    setSelectedPaymentType: handleSetSelectedPaymentType,
    triggerSuccessModal: () => setShowSuccessModal(true),
    closePaymentModal: () => setShowPaymentModal(false),
    coachingEndDate: coachingEndDateFormatted,
    nextBillingDate: nextBillingDateFormatted,
    isReactivateAllowed,
    showReactivateModal,
    setShowReactivateModal,
    isReactivating,
    reactivateError,
    handleConfirmReactivateAndPurchase,
  };
};
